import React, { useState, useEffect } from "react";
import GlobalStyles, { CommonClasses } from "../../global/GlobalStyle";
import { useAuth } from "../../hooks/useAuth";
import { useAlert } from "../../contexts/AlertContext";
import {
  MdVerifiedUser,
  MdUpload,
  MdCheckCircle,
  MdCancel,
  MdPerson,
  MdCreditCard,
  MdLocationOn,
  MdWarning,
  MdInfo,
  MdPhone,
  MdFlag,
} from "react-icons/md";
import { kycAPI } from "../../services/api";
import countries from "../../arrays/countries";

const KYCVerification = () => {
  const { user, updateUser } = useAuth();
  const { addAlert } = useAlert();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    phone_number: "",
    address: "",
    country: "",
  });
  const [documents, setDocuments] = useState({
    identity_document: null as File | null,
  });
  const [uploadProgress, setUploadProgress] = useState({
    identity_document: 0,
  });

  const VerifiedIcon = MdVerifiedUser as React.FC<
    React.SVGProps<SVGSVGElement>
  >;
  const UploadIcon = MdUpload as React.FC<React.SVGProps<SVGSVGElement>>;
  const CheckIcon = MdCheckCircle as React.FC<React.SVGProps<SVGSVGElement>>;
  const CancelIcon = MdCancel as React.FC<React.SVGProps<SVGSVGElement>>;
  const PersonIcon = MdPerson as React.FC<React.SVGProps<SVGSVGElement>>;
  const CardIcon = MdCreditCard as React.FC<React.SVGProps<SVGSVGElement>>;
  const LocationIcon = MdLocationOn as React.FC<React.SVGProps<SVGSVGElement>>;
  const WarningIcon = MdWarning as React.FC<React.SVGProps<SVGSVGElement>>;
  const InfoIcon = MdInfo as React.FC<React.SVGProps<SVGSVGElement>>;
  const PhoneIcon = MdPhone as React.FC<React.SVGProps<SVGSVGElement>>;
  const FlagIcon = MdFlag as React.FC<React.SVGProps<SVGSVGElement>>;

  // Initialize form data when user data is available
  useEffect(() => {
    if (user?.kyc_data) {
      setFormData({
        phone_number: user.kyc_data.phone_number || "",
        address: user.kyc_data.address || "",
        country: user.kyc_data.country || "",
      });
    }
  }, [user]);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    documentType: string
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        addAlert({ type: "error", message: "File size must be less than 5MB" });
        return;
      }

      // Check file type
      const allowedTypes = [
        "image/jpeg",
        "image/png",
        "image/jpg",
        "application/pdf",
      ];
      if (!allowedTypes.includes(file.type)) {
        addAlert({
          type: "error",
          message: "Only JPEG, PNG, and PDF files are allowed",
        });
        return;
      }

      setDocuments((prev) => ({
        ...prev,
        [documentType]: file,
      }));

      // Simulate upload progress
      simulateUploadProgress(documentType);
    }
  };

  const simulateUploadProgress = (documentType: string) => {
    let progress = 0;
    const interval = setInterval(() => {
      progress += 10;
      setUploadProgress((prev) => ({
        ...prev,
        [documentType]: progress,
      }));

      if (progress >= 100) {
        clearInterval(interval);
      }
    }, 100);
  };

  const removeDocument = (documentType: string) => {
    setDocuments((prev) => ({
      ...prev,
      [documentType]: null,
    }));
    setUploadProgress((prev) => ({
      ...prev,
      [documentType]: 0,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Validate required fields
    const requiredFields = ["phone_number", "address", "country"];
    const missingFields = requiredFields.filter(
      (field) => !formData[field as keyof typeof formData]
    );

    if (missingFields.length > 0) {
      addAlert({ type: "error", message: "Please fill all required fields" });
      setIsSubmitting(false);
      return;
    }

    if (!documents.identity_document) {
      addAlert({
        type: "error",
        message: "Please upload your identity document",
      });
      setIsSubmitting(false);
      return;
    }

    try {
      // Create FormData for file upload
      const submitData = new FormData();

      // Append form data
      Object.entries(formData).forEach(([key, value]) => {
        submitData.append(key, value);
      });

      // Append file
      if (documents.identity_document) {
        submitData.append("identity_document", documents.identity_document);
      }

      // REAL API CALL - replace the mock call with this:
      const response = await kycAPI.submitKYC(submitData);

      // Update user state with response from backend
      updateUser(response.data.user);

      addAlert({
        type: "success",
        message: "KYC verification submitted for review!",
      });
    } catch (error: any) {
      addAlert({
        type: "error",
        message:
          error.response?.data?.message || "Failed to submit KYC verification",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get KYC status info
  const getKYCStatusInfo = () => {
    const status = user?.kyc_status || "not_started";

    // If kyc_data is null but status is pending, treat as not started
    if (status === "pending" && !user?.kyc_data) {
      return {
        icon: <VerifiedIcon className="w-6 h-6 text-gray-500" />,
        title: "Not Started",
        message: "Complete KYC verification to unlock full platform features.",
        color: "text-gray-600",
        bgColor: "bg-gray-50 dark:bg-gray-900/20",
        borderColor: "border-gray-200 dark:border-gray-800",
      };
    }

    switch (status) {
      case "verified":
        return {
          icon: <CheckIcon className="w-6 h-6 text-green-500" />,
          title: "KYC Verified",
          message: "Your identity has been successfully verified.",
          color: "text-green-600",
          bgColor: "bg-green-50 dark:bg-green-900/20",
          borderColor: "border-green-200 dark:border-green-800",
        };
      case "pending":
        return {
          icon: <VerifiedIcon className="w-6 h-6 text-yellow-500" />,
          title: "Under Review",
          message: "Your KYC application is being reviewed by our team.",
          color: "text-yellow-600",
          bgColor: "bg-yellow-50 dark:bg-yellow-900/20",
          borderColor: "border-yellow-200 dark:border-yellow-800",
        };
      case "rejected":
        return {
          icon: <CancelIcon className="w-6 h-6 text-red-500" />,
          title: "Verification Failed",
          message:
            "Your KYC application was rejected. Please review and resubmit.",
          color: "text-red-600",
          bgColor: "bg-red-50 dark:bg-red-900/20",
          borderColor: "border-red-200 dark:border-red-800",
        };
      default:
        return {
          icon: <VerifiedIcon className="w-6 h-6 text-gray-500" />,
          title: "Not Started",
          message:
            "Complete KYC verification to unlock full platform features.",
          color: "text-gray-600",
          bgColor: "bg-gray-50 dark:bg-gray-900/20",
          borderColor: "border-gray-200 dark:border-gray-800",
        };
    }
  };

  const statusInfo = getKYCStatusInfo();

  // If KYC is already verified and has data, show success page
  if (user?.kyc_status === "verified" && user.kyc_data) {
    return (
      <div className="space-y-6">
        <div>
          <h1
            className={`${CommonClasses.textXl} font-bold ${CommonClasses.textGrayStrong} mb-2`}
          >
            KYC Verification
          </h1>
          <p className={`${CommonClasses.textGray}`}>
            Your identity verification status and information.
          </p>
        </div>

        <div className={`${GlobalStyles.sectionWrap} text-center py-12`}>
          <div className="w-24 h-24 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckIcon className="w-12 h-12 text-green-500" />
          </div>
          <h2
            className={`${CommonClasses.textXl} font-bold text-green-600 mb-4`}
          >
            Identity Verified Successfully!
          </h2>
          <p className={`${CommonClasses.textGray} mb-8 max-w-md mx-auto`}>
            Your KYC verification has been completed successfully. You now have
            full access to all platform features and higher transaction limits.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-2xl mx-auto">
            <div className="text-center p-6 border border-green-200 dark:border-green-800 rounded-lg">
              <PersonIcon className="w-10 h-10 text-green-500 mx-auto mb-3" />
              <p className="font-semibold mb-2">Identity Verified</p>
              <p
                className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}
              >
                Your identity documents have been verified
              </p>
            </div>
            <div className="text-center p-6 border border-green-200 dark:border-green-800 rounded-lg">
              <CardIcon className="w-10 h-10 text-green-500 mx-auto mb-3" />
              <p className="font-semibold mb-2">Documents Approved</p>
              <p
                className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}
              >
                All submitted documents are approved
              </p>
            </div>
            <div className="text-center p-6 border border-green-200 dark:border-green-800 rounded-lg">
              <VerifiedIcon className="w-10 h-10 text-green-500 mx-auto mb-3" />
              <p className="font-semibold mb-2">Level 3 Access</p>
              <p
                className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}
              >
                Full platform access unlocked
              </p>
            </div>
          </div>

          <div className="mt-8 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg max-w-md mx-auto">
            <div className={`${CommonClasses.flexStart} gap-3`}>
              <InfoIcon className="w-5 h-5 text-blue-500 flex-shrink-0" />
              <p
                className={`${CommonClasses.textSm} text-blue-700 dark:text-blue-300`}
              >
                Your account has been upgraded to Level 3 with enhanced features
                and higher limits.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // If KYC is pending and has data, show pending status
  if (user?.kyc_status === "pending" && user.kyc_data) {
    return (
      <div className="space-y-6">
        <div>
          <h1
            className={`${CommonClasses.textXl} font-bold ${CommonClasses.textGrayStrong} mb-2`}
          >
            KYC Verification
          </h1>
          <p className={`${CommonClasses.textGray}`}>
            Your identity verification status.
          </p>
        </div>

        <div className={`${GlobalStyles.sectionWrap} text-center py-12`}>
          <div className="w-24 h-24 bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
            <VerifiedIcon className="w-12 h-12 text-yellow-500" />
          </div>
          <h2
            className={`${CommonClasses.textXl} font-bold text-yellow-600 mb-4`}
          >
            Verification Under Review
          </h2>
          <p className={`${CommonClasses.textGray} mb-6 max-w-md mx-auto`}>
            Your KYC application is currently being reviewed by our verification
            team. This process usually takes 24-48 hours.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-xl mx-auto">
            <div className="text-center p-6 border border-yellow-200 dark:border-yellow-800 rounded-lg">
              <PersonIcon className="w-10 h-10 text-yellow-500 mx-auto mb-3" />
              <p className="font-semibold mb-2">Under Review</p>
              <p
                className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}
              >
                Your documents are being verified
              </p>
            </div>
            <div className="text-center p-6 border border-yellow-200 dark:border-yellow-800 rounded-lg">
              <CardIcon className="w-10 h-10 text-yellow-500 mx-auto mb-3" />
              <p className="font-semibold mb-2">Processing</p>
              <p
                className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}
              >
                Verification in progress
              </p>
            </div>
          </div>

          <div className="mt-8 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg max-w-md mx-auto">
            <div className={`${CommonClasses.flexStart} gap-3`}>
              <InfoIcon className="w-5 h-5 text-blue-500 flex-shrink-0" />
              <p
                className={`${CommonClasses.textSm} text-blue-700 dark:text-blue-300`}
              >
                You will receive an email notification once your verification is
                complete.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show KYC form for new submissions or rejected applications
  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1
          className={`${CommonClasses.textXl} font-bold ${CommonClasses.textGrayStrong} mb-2`}
        >
          KYC Verification
        </h1>
        <p className={`${CommonClasses.textGray}`}>
          Verify your identity to unlock full platform features and higher
          limits.
        </p>
      </div>

      {/* Status Banner */}
      {user?.kyc_status && (
        <div
          className={`p-4 rounded-lg border ${statusInfo.bgColor} ${statusInfo.borderColor}`}
        >
          <div className={`${CommonClasses.flexStart} gap-3`}>
            {statusInfo.icon}
            <div>
              <h3 className={`font-semibold ${statusInfo.color}`}>
                {statusInfo.title}
              </h3>
              <p
                className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}
              >
                {statusInfo.message}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* KYC Form */}
        <div className={`${GlobalStyles.sectionWrap}`}>
          <h2
            className={`${CommonClasses.textLg} font-semibold ${CommonClasses.textGrayStrong} mb-4`}
          >
            Personal Information
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Phone Number */}
            <div className={GlobalStyles.formGroup}>
              <label htmlFor="phone_number" className={GlobalStyles.label}>
                Phone Number *
              </label>
              <div className="flex gap-3">
                <div className="w-1/3">
                  <div className={GlobalStyles.inputWrapper}>
                    <FlagIcon className={GlobalStyles.inputIcon} />
                    <select
                      id="country"
                      name="country"
                      value={formData.country}
                      onChange={handleInputChange}
                      className={`${GlobalStyles.input} border-0 cursor-pointer`}
                      required
                    >
                      <option value="">Select Country</option>
                      {countries.map((country) => (
                        <option key={country.code} value={country.code}>
                          {country.name} ({country.phoneCode})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="flex-1">
                  <div className={GlobalStyles.inputWrapper}>
                    <PhoneIcon className={GlobalStyles.inputIcon} />
                    <input
                      id="phone_number"
                      name="phone_number"
                      type="tel"
                      value={formData.phone_number}
                      onChange={handleInputChange}
                      className={GlobalStyles.input}
                      placeholder="Phone number"
                      required
                    />
                  </div>
                </div>
              </div>
              <p
                className={`${CommonClasses.textXs} ${CommonClasses.textGray} mt-1`}
              >
                We'll use this for account verification and security alerts
              </p>
            </div>

            {/* Address */}
            <div className={GlobalStyles.formGroup}>
              <label htmlFor="address" className={GlobalStyles.label}>
                Residential Address *
              </label>
              <div className={GlobalStyles.inputWrapper}>
                <LocationIcon className={GlobalStyles.inputIcon} />
                <input
                  id="address"
                  name="address"
                  type="text"
                  value={formData.address}
                  onChange={handleInputChange}
                  className={GlobalStyles.input}
                  placeholder="Enter your complete residential address"
                  required
                />
              </div>
              <p
                className={`${CommonClasses.textXs} ${CommonClasses.textGray} mt-1`}
              >
                Your current residential address for verification purposes
              </p>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className={`${GlobalStyles.buttonPrimary} !w-full ${
                isSubmitting ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Submitting...
                </>
              ) : (
                "Submit for Verification"
              )}
            </button>
          </form>
        </div>

        {/* Document Upload */}
        <div className={`${GlobalStyles.sectionWrap}`}>
          <h2
            className={`${CommonClasses.textLg} font-semibold ${CommonClasses.textGrayStrong} mb-4`}
          >
            Identity Verification
          </h2>

          <div className="space-y-6">
            {/* Identity Document */}
            <div className={GlobalStyles.formGroup}>
              <label
                className={`${GlobalStyles.label} flex items-center gap-2`}
              >
                Identity Document *
                {documents.identity_document &&
                  uploadProgress.identity_document === 100 && (
                    <CheckIcon className="w-4 h-4 text-green-500" />
                  )}
              </label>
              <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4 text-center">
                <input
                  type="file"
                  onChange={(e) => handleFileChange(e, "identity_document")}
                  accept=".jpg,.jpeg,.png,.pdf"
                  className="hidden"
                  id="identity_document"
                />
                <label
                  htmlFor="identity_document"
                  className="cursor-pointer block"
                >
                  <UploadIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    {documents.identity_document
                      ? documents.identity_document.name
                      : "Upload government-issued ID (Passport, Driver's License, National ID)"}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    JPEG, PNG, or PDF (Max 5MB)
                  </p>
                </label>
                {uploadProgress.identity_document > 0 &&
                  uploadProgress.identity_document < 100 && (
                    <div className="mt-2">
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-primary h-2 rounded-full transition-all duration-300"
                          style={{
                            width: `${uploadProgress.identity_document}%`,
                          }}
                        ></div>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Uploading... {uploadProgress.identity_document}%
                      </p>
                    </div>
                  )}
                {documents.identity_document &&
                  uploadProgress.identity_document === 100 && (
                    <button
                      type="button"
                      onClick={() => removeDocument("identity_document")}
                      className="mt-2 text-red-600 hover:text-red-800 text-sm"
                    >
                      Remove File
                    </button>
                  )}
              </div>
            </div>

            {/* Accepted Documents */}
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <div className={`${CommonClasses.flexStart} gap-3`}>
                <InfoIcon className="w-5 h-5 text-blue-500 flex-shrink-0" />
                <div>
                  <p
                    className={`${CommonClasses.textSm} font-medium text-blue-800 dark:text-blue-200`}
                  >
                    Accepted Documents
                  </p>
                  <ul
                    className={`${CommonClasses.textXs} text-blue-700 dark:text-blue-300 mt-1 list-disc list-inside`}
                  >
                    <li>Passport</li>
                    <li>Driver's License</li>
                    <li>National ID Card</li>
                    <li>State ID Card</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Security Warning */}
            <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
              <div className={`${CommonClasses.flexStart} gap-3`}>
                <WarningIcon className="w-5 h-5 text-yellow-500 flex-shrink-0" />
                <div>
                  <p
                    className={`${CommonClasses.textSm} font-medium text-yellow-800 dark:text-yellow-200`}
                  >
                    Security Notice
                  </p>
                  <p
                    className={`${CommonClasses.textXs} text-yellow-700 dark:text-yellow-300 mt-1`}
                  >
                    All documents are encrypted and stored securely. We never
                    share your personal information with third parties. Your
                    data is protected by bank-level security measures.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Benefits of KYC */}
      <div className={`${GlobalStyles.sectionWrap}`}>
        <h2
          className={`${CommonClasses.textLg} font-semibold ${CommonClasses.textGrayStrong} mb-4`}
        >
          Benefits of KYC Verification
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center p-6 border border-gray-200 dark:border-gray-700 rounded-lg">
            <VerifiedIcon className="w-12 h-12 text-primary mx-auto mb-3" />
            <h3 className="font-semibold text-lg mb-2">Higher Limits</h3>
            <p className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}>
              Increased deposit and withdrawal limits for verified accounts
            </p>
          </div>
          <div className="text-center p-6 border border-gray-200 dark:border-gray-700 rounded-lg">
            <CardIcon className="w-12 h-12 text-primary mx-auto mb-3" />
            <h3 className="font-semibold text-lg mb-2">Full Access</h3>
            <p className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}>
              Access to all platform features, services, and advanced trading
              tools
            </p>
          </div>
          <div className="text-center p-6 border border-gray-200 dark:border-gray-700 rounded-lg">
            <LocationIcon className="w-12 h-12 text-primary mx-auto mb-3" />
            <h3 className="font-semibold text-lg mb-2">Enhanced Security</h3>
            <p className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}>
              Additional security features and protection for your account and
              funds
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KYCVerification;
